/* Native C01: read-only geometry audit, not an automatic content fitter.
 * Does not load the frozen Canvas sizing adapter or touch storefront data. */
(() => {
  'use strict';
  if (window.AstroBrewDawnHeroSizing) return;
  const root = document.documentElement;
  const observed = new Set();
  let frame = 0;
  let mainObserver;
  let observedMain;
  const matchesClass = (element, pattern) =>
    Array.from(element.classList).some((name) => pattern.test(name));
  const isHero = (element) =>
    element.hasAttribute('data-ab-hero') ||
    element.hasAttribute('data-ab-canvas-banner') ||
    matchesClass(element, /(?:^hero$|[-_]hero$|^abpd-product$|^astrobrew-about--intro$)/);
  function schedule() {
    if (!frame) frame = window.requestAnimationFrame(update);
  }
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
  function observe(element) {
    if (!element || observed.has(element)) return;
    observed.add(element);
    resizeObserver?.observe(element);
  }
  function setPixels(element, name, value) {
    const next = `${Math.round(value * 100) / 100}px`;
    if (element.style.getPropertyValue(name) !== next) element.style.setProperty(name, next);
  }
  function measureHeader(selector) {
    return Array.from(document.querySelectorAll(selector))
      .filter((element, index, elements) => !elements.some((other, otherIndex) =>
        otherIndex !== index && other.contains(element)))
      .reduce((height, element) => {
        observe(element);
        return height + element.getBoundingClientRect().height;
      }, 0);
  }
  function audit(hero) {
    hero.setAttribute('data-ab-dawn-hero', '');
    observe(hero);
    // Gallery + order interfaces are introductions, not capped promotions.
    const introduction = hero.matches('.abpd-product') || !!hero.querySelector('product-form, astrobrew-product-form, form[action*="/cart/add"]');
    hero.querySelectorAll('[class]').forEach((element) => {
      if (matchesClass(element, /hero(?:[-_]|__)(?:copy|content)$/)) {
        element.setAttribute('data-ab-dawn-hero-copy', '');
      }
    });
    hero.querySelectorAll('img, video').forEach((image) => {
      if (!image.closest('.ab-home-hero__slide:not(.is-pisces), .abpd-thumbnails, .abpd-service-rail')) {
        image.setAttribute('data-ab-dawn-hero-image', '');
      }
    });
    const layout = hero.querySelector('[class$="hero-inner"], [class$="hero-grid"]') || hero;
    const style = window.getComputedStyle(layout);
    const columns = style.gridTemplateColumns.split(' ').filter(Boolean);
    const stacked = (style.display === 'grid' && columns.length === 1) ||
      (style.display === 'flex' && style.flexDirection === 'column');
    // Homepage overlays have a single grid cell, not a stacked tablet layout.
    const horizontal = hero.matches('.ab-home-hero') ?
      window.matchMedia('(min-width: 761px)').matches :
      hero.matches('.machine-d-pixel.hero') ? window.matchMedia('(min-width: 701px)').matches :
      style.display === 'grid' || style.display === 'flex' ? !stacked :
      window.matchMedia('(min-width: 761px)').matches;
    const heroBox = hero.getBoundingClientRect();
    // Owner-approved homepage-only exception: retain tallest natural slide.
    const homepageException = hero.matches('astrobrew-home-hero.ab-home-hero');
    hero.dataset.abDawnHeroKind = introduction ? 'product-introduction' : 'promotion';
    if (homepageException) hero.dataset.abDawnHeroHeightException = 'homepage-natural-slideshow';
    else hero.removeAttribute('data-ab-dawn-hero-height-exception');
    if (!homepageException && !introduction && horizontal && heroBox.height > 600.5) {
      hero.dataset.abDawnHeroConflict = 'owner-approval-required';
    } else {
      hero.removeAttribute('data-ab-dawn-hero-conflict');
    }
  }
  function update() {
    frame = 0;
    for (const element of observed) {
      if (!element.isConnected) { resizeObserver?.unobserve(element); observed.delete(element); }
    }
    setPixels(root, '--ab-announcement-height', measureHeader('.announcement-bar-section'));
    setPixels(root, '--ab-navigation-height', measureHeader('.section-header'));
    const main = document.getElementById('MainContent');
    if (!main) return;
    Array.from(main.querySelectorAll('[class], [data-ab-hero], [data-ab-canvas-banner]')).filter(isHero).forEach(audit);
    if (main !== observedMain) {
      mainObserver?.disconnect();
      mainObserver = new MutationObserver(schedule);
      mainObserver.observe(main, { childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'hidden'] });
      observedMain = main;
    }
  }
  function start() {
    update();
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
  }
  window.AstroBrewDawnHeroSizing = { refresh: schedule };
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  document.addEventListener('shopify:block:select', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
